import React, { useRef, useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { HiOutlineBell } from "react-icons/hi";
import { resolveNotificationPath } from "../../utils/notificationPath";
import {
  canUseNotificationContextFilter,
  filterNotificationsByContext,
  contextForRole,
} from "../../utils/notificationContext";

const NotificationDropdown = ({
  isNotificationOpen,
  setIsNotificationOpen,
  unreadCount,
  recentNotifications,
  notifications,
  handleNotificationClick,
  handleMarkAllAsRead,
  formatNotificationTime,
}) => {
  const notificationButtonRef = useRef(null);
  const notificationMenuRef = useRef(null);

  // All / Unread / Read filter, consistent with the /notifications page.
  const [filter, setFilter] = useState("all");
  const authRoles = useSelector((state) => state.auth && state.auth.roles);
  const currentRole = useSelector((state) => state.auth && state.auth.currentRole);
  const showContextFilter = canUseNotificationContextFilter(authRoles);
  // W1-02 + hard split (Marin, 2026-07-30): the profile-context view follows the
  // profile switch instead of defaulting to "All". Kept identical to the
  // /notifications page and the mobile sidebar — the three bell surfaces must
  // never disagree about which profile the user is looking at.
  const contextFilter = contextForRole(currentRole);

  const readCount = Math.max(0, notifications.length - unreadCount);


  // Apply the filters to the most recent notifications shown in the bell.
  const visibleNotifications = useMemo(() => {
    const inContext = showContextFilter
      ? filterNotificationsByContext(recentNotifications, contextFilter)
      : recentNotifications;
    if (filter === "unread") {
      return inContext.filter((n) => !n.read);
    }
    if (filter === "read") {
      return inContext.filter((n) => n.read);
    }
    return inContext;
  }, [filter, contextFilter, showContextFilter, recentNotifications]);

  // Close dropdown on outside click or Escape key
  useEffect(() => {
    if (!isNotificationOpen) return;

    const handleClickOutside = (e) => {
      if (
        notificationMenuRef.current &&
        !notificationMenuRef.current.contains(e.target) &&
        notificationButtonRef.current &&
        !notificationButtonRef.current.contains(e.target)
      ) {
        setIsNotificationOpen(false);
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setIsNotificationOpen(false);
        notificationButtonRef.current?.focus();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isNotificationOpen, setIsNotificationOpen]);

  return (
    <div className="relative">
      <button
        ref={notificationButtonRef}
        aria-haspopup="true"
        aria-expanded={isNotificationOpen}
        aria-controls="notification-menu"
        className="w-[38px] h-[38px] rounded-[10px] flex items-center justify-center text-[#4B5563] hover:bg-[#F3F6FB] transition-colors relative focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
        onClick={() => {
          const next = !isNotificationOpen;
          setIsNotificationOpen(next);
          // On-demand notifications websocket connect/disconnect
          if (next) {
            window.dispatchEvent(new CustomEvent("notifications:connect"));
          } else {
            window.dispatchEvent(new CustomEvent("notifications:disconnect"));
          }
        }}
        onKeyDown={(e) => {
          // Open with Enter/Space
          if (
            (e.key === "Enter" || e.key === " ") &&
            !isNotificationOpen
          ) {
            e.preventDefault();
            setIsNotificationOpen(true);
          }
        }}
      >
        <HiOutlineBell className="w-[21px] h-[21px]" />

        {/* Unread badge — design-styled dot carrying the count */}
        {unreadCount > 0 && (
          <span className="absolute top-[6px] right-[6px] min-w-[16px] h-[16px] px-1 bg-[#2283FF] text-white text-[10px] font-semibold leading-none rounded-full flex items-center justify-center border-[1.5px] border-white">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {/* Notification Dropdown */}
      {isNotificationOpen && (
        <div
          ref={notificationMenuRef}
          id="notification-menu"
          role="menu"
          aria-label="Notifications"
          className="absolute right-0 mt-2 w-[400px] bg-white border rounded-2xl shadow-lg py-4 z-50 max-h-[500px] focus:outline-none"
          tabIndex={-1}
          onKeyDown={(e) => {
            if (e.key === "ArrowUp" || e.key === "ArrowDown") {
              e.preventDefault();
              const items = notificationMenuRef.current?.querySelectorAll(
                "[data-notification-item]"
              );
              if (!items || items.length === 0) return;

              const currentIndex = Array.from(items).findIndex(
                (item) => item === document.activeElement
              );

              let nextIndex;
              if (e.key === "ArrowDown") {
                nextIndex =
                  currentIndex < items.length - 1 ? currentIndex + 1 : 0;
              } else {
                nextIndex =
                  currentIndex > 0 ? currentIndex - 1 : items.length - 1;
              }

              items[nextIndex]?.focus();
            }
          }}
        >
          {/* Header */}
          <div className="px-4 pb-3 border-b flex items-center justify-between">
            <h3 className="text-lg font-semibold">Notifications</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  className="text-xs text-blue-600 hover:text-blue-800"
                >
                  Mark all read
                </button>
              )}
            </div>
          </div>

          {/* Filter Tabs */}
          <div className="px-4 pt-3 pb-2">
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
              {[
                { key: "all", label: "All", count: notifications.length },
                { key: "unread", label: "Unread", count: unreadCount },
                { key: "read", label: "Read", count: readCount },
              ].map(({ key, label, count }) => (
                <button
                  key={key}
                  onClick={() => setFilter(key)}
                  className={`flex-1 px-2 py-1.5 rounded-md text-xs font-medium transition-colors ${
                    filter === key
                      ? "bg-white text-blue-600 shadow-sm"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  {label} ({count})
                </button>
              ))}
            </div>
          </div>

          {/* The Agency / Personal tab row used to sit here. Removed on
              Marin's call (2026-07-31): the profile switch in the header
              already says which profile you are in, so the bell simply shows
              that profile's notifications. */}

          {/* Notifications List */}
          <div className="max-h-[300px] overflow-y-auto">
            {visibleNotifications.length > 0 ? (
              visibleNotifications.map((notification) => (
                <div
                  key={notification.id}
                  data-notification-item
                  tabIndex={0}
                  role="menuitem"
                  className={`px-4 py-3 hover:bg-gray-50 cursor-pointer border-l-4 focus:outline-none focus:bg-gray-100 ${
                    notification.read
                      ? "border-transparent"
                      : "border-blue-500 bg-blue-50"
                  }`}
                  onClick={() => handleNotificationClick(notification.id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleNotificationClick(notification.id);
                    }
                  }}
                >
                  <div className="flex items-start gap-3">
                    {/* Club logo (when present) with a clean icon fallback */}
                    {notification.image_url ? (
                      <img
                        src={notification.image_url}
                        alt=""
                        className="w-6 h-6 rounded-full object-cover flex-shrink-0 mt-0.5 border border-gray-200 bg-white"
                      />
                    ) : (
                      <div className="w-6 h-6 rounded-full flex-shrink-0 mt-0.5 bg-gray-100 flex items-center justify-center">
                        <HiOutlineBell className="w-3.5 h-3.5 text-gray-400" />
                      </div>
                    )}

                    {/* Unread dot */}
                    <div
                      className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                        notification.read ? "bg-gray-300" : "bg-blue-500"
                      }`}
                    />

                    {/* Notification Content */}
                    <div className="flex-1 min-w-0">
                      <p
                        className={`text-sm ${
                          notification.read
                            ? "text-gray-600"
                            : "text-gray-900 font-medium"
                        }`}
                      >
                        {notification.text}
                      </p>
                      <div className="flex items-center justify-between mt-1">
                        <p className="text-xs text-gray-500">
                          {formatNotificationTime(notification.created_at)}
                        </p>
                        {resolveNotificationPath(notification) && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleNotificationClick(notification.id);
                            }}
                            className="text-xs text-blue-600 hover:text-blue-800 font-medium ml-2 flex-shrink-0"
                          >
                            View
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="px-4 py-8 text-center text-gray-500">
                <HiOutlineBell className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>
                  {showContextFilter && contextFilter !== "all"
                    ? "No notifications for this filter"
                    : filter === "unread"
                      ? "No unread notifications"
                      : filter === "read"
                        ? "No read notifications"
                        : "No notifications yet"}
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 10 && (
            <div className="px-4 pt-3 border-t">
              <Link
                to="/notifications"
                className="block w-full text-center py-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
                onClick={() => setIsNotificationOpen(false)}
              >
                View All Notifications ({notifications.length})
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;
